import {useEffect, useMemo, useState} from 'react'
import {Card, Spinner, Button, Row, Col, Form, Modal} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {TWILIO_NUMBERS_INVENTORY} from '../../gql/queries/twilioBilling'
import {RELEASE_TWILIO_NUMBER, ADD_NUMBERS_TO_POOL} from '../../gql/mutations/twilioBilling'
import {useTableSort} from '../../helpers/useTableSort'
import {US_STATES} from '../../helpers/catalogs'
import {useGetUser} from '../../store/userStore'

const money = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

// 2-letter code → full state name (from the shared US_STATES catalog).
const STATE_NAME: Record<string, string> = Object.fromEntries(
  US_STATES.map((s: any) => [s.code || s.value, s.name || s.label])
)
const stateName = (code: string) => (code ? STATE_NAME[code] || code : '—')
const dateOnly = (s: string) => (s ? s.slice(0, 10) : '—')

type Classification = 'pool' | 'policydial' | 'messaging' | 'unclassified'
type InvNumber = {
  phone_number: string
  friendly_name: string
  type: string
  state: string
  rental_estimate: number
  date_created: string
  owner: string
  group: string
  organization: string
  classification: Classification
  last_used: string
  stale: boolean
}
type Inventory = {
  count: number
  total_rental_estimate: number
  counts: Record<string, number>
  unclassified_count: number
  stale_pool_count: number
  numbers: InvNumber[]
}

const CLASS_LABEL: Record<Classification, string> = {
  pool: 'Local-presence pool',
  policydial: 'PolicyDial (agent)',
  messaging: 'SMS / WhatsApp',
  unclassified: 'Unclassified',
}
const CLASS_BADGE: Record<Classification, string> = {
  pool: 'badge-light-primary',
  policydial: 'badge-light-success',
  messaging: 'badge-light-info',
  unclassified: 'badge-light-warning',
}

const PAGE_SIZE = 15

// Billing → Inventory numbers: the owned-number inventory (with cleanup) plus an
// "Add number to pool" button that opens a buy modal; on success the modal lists
// the purchased numbers with their state and cost, then refetches the inventory.
export const NumbersInventoryTab = () => {
  const {data: iData, loading: iLoading, error: iError, refetch: refetchInv} = useQuery(TWILIO_NUMBERS_INVENTORY, {
    fetchPolicy: 'cache-and-network',
  })
  const [releaseNumber] = useMutation(RELEASE_TWILIO_NUMBER)
  const [addToPool, {loading: buyingPool}] = useMutation(ADD_NUMBERS_TO_POOL)

  const inventory: Inventory | undefined = iData?.twilioNumbersInventory

  // The Organization column is a platform-superadmin view (org-admins are already
  // scoped to their own org by the backend, so it would just repeat their org).
  const userGroups = useGetUser((s: any) => s.user?.groups)
  const isSuperAdmin = Array.isArray(userGroups) && userGroups.includes('SuperAdministrador')

  const [reviewOnly, setReviewOnly] = useState(false)
  const [classFilter, setClassFilter] = useState<Classification | ''>('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState('')

  // "Add number to pool" modal + the purchase result shown once it finishes.
  const [showBuy, setShowBuy] = useState(false)
  const [bought, setBought] = useState<any>(null)
  const [poolState, setPoolState] = useState('')
  const [poolArea, setPoolArea] = useState('')
  const [poolQty, setPoolQty] = useState('1')

  const {onSort, sortRows, arrow} = useTableSort<any>('', 'desc')

  const filtered = useMemo(() => {
    let list = inventory?.numbers ?? []
    if (classFilter) list = list.filter((n) => n.classification === classFilter)
    if (reviewOnly) list = list.filter((n) => n.classification === 'unclassified' || n.stale)
    const q = search.replace(/\D/g, '')
    if (q) list = list.filter((n) => n.phone_number.replace(/\D/g, '').includes(q))
    return sortRows(list)
  }, [inventory, reviewOnly, search, classFilter, sortRows])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  useEffect(() => {
    setPage(1)
  }, [search, reviewOnly, classFilter])

  const openBuy = () => {
    setBought(null)
    setPoolArea('')
    setPoolQty('1')
    setShowBuy(true)
  }
  const closeBuy = () => {
    setShowBuy(false)
    setBought(null)
  }

  const handleAddToPool = async () => {
    const qty = Math.max(1, Math.min(10, parseInt(poolQty || '1', 10) || 1))
    if (!poolState) {
      toast.warning('Pick a state first.')
      return
    }
    try {
      const {data} = await addToPool({
        variables: {state: poolState, areaCode: poolArea || null, quantity: qty},
      })
      const res = data?.addNumbersToPool
      if (res?.success) {
        toast.success(res.message || 'Numbers added to the pool.')
        ;(res.warnings || []).forEach((w: string) => toast.warning(w))
        setBought(res.result) // { purchased:[{e164,state_id,cost}], total_cost, count, warnings }
        await refetchInv()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not add numbers to the pool')
    }
  }

  const handleRelease = async (n: InvNumber) => {
    const warn =
      n.classification === 'messaging'
        ? '\n\n⚠️ This number is used for SMS/WhatsApp. Releasing it will break that.'
        : n.classification === 'pool'
        ? '\n\nIt will be removed from the local-presence dialing pool.'
        : ''
    if (!window.confirm(`Release ${n.phone_number}? This permanently gives up the number on Twilio and stops its ~$${n.rental_estimate}/mo charge.${warn}`))
      return
    setBusy(n.phone_number)
    try {
      const {data} = await releaseNumber({variables: {phoneNumber: n.phone_number}})
      if (data?.releaseTwilioNumber?.success) {
        toast.success(`${n.phone_number} released.`)
        ;(data.releaseTwilioNumber.warnings || []).forEach((w: string) => toast.warning(w))
        await refetchInv()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not release the number')
    } finally {
      setBusy('')
    }
  }

  const exportCsv = () => {
    const rows = inventory?.numbers ?? []
    if (!rows.length) {
      toast.info('Nothing to export yet.')
      return
    }
    const header = ['Phone Number', 'Friendly Name', 'Type', 'State', 'Monthly Rental (est.)', 'Purchased', 'Used For', 'Owner', 'Group', ...(isSuperAdmin ? ['Organization'] : []), 'Last Used', 'Stale']
    const body = rows.map((n) => [
      n.phone_number,
      `"${(n.friendly_name || '').replace(/"/g, '""')}"`,
      n.type,
      `"${stateName(n.state)}"`,
      n.rental_estimate,
      n.date_created,
      n.classification,
      n.owner || '',
      `"${(n.group || '').replace(/"/g, '""')}"`,
      ...(isSuperAdmin ? [`"${(n.organization || '').replace(/"/g, '""')}"`] : []),
      n.last_used || '',
      n.stale ? 'yes' : 'no',
    ])
    const csv = [header.join(','), ...body.map((r) => r.join(','))].join('\n')
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'twilio-numbers.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (iError) {
    return (
      <Card className='mt-5'>
        <Card.Body className='text-center py-10'>
          <h4 className='mb-2'>Numbers inventory unavailable</h4>
          <p className='text-muted mb-0'>{iError.message}</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className='mt-5'>
      <Card>
          <Card.Header className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
            <div>
              <Card.Title className='mb-1'>Numbers inventory {inventory ? `(${inventory.count})` : ''}</Card.Title>
              {inventory && (
                <div className='d-flex flex-wrap gap-2 align-items-center'>
                  {(['pool', 'messaging', 'policydial', 'unclassified'] as Classification[]).map((k) =>
                    inventory.counts?.[k] ? (
                      <span
                        key={k}
                        role='button'
                        title={classFilter === k ? 'Clear filter' : `Filter by ${CLASS_LABEL[k]}`}
                        onClick={() => setClassFilter((prev) => (prev === k ? '' : k))}
                        className={`badge ${CLASS_BADGE[k]}`}
                        style={{
                          cursor: 'pointer',
                          outline: classFilter === k ? '2px solid currentColor' : 'none',
                          opacity: classFilter && classFilter !== k ? 0.45 : 1,
                        }}
                      >
                        {CLASS_LABEL[k]}: {inventory.counts[k]}
                      </span>
                    ) : null
                  )}
                  {inventory.stale_pool_count > 0 && (
                    <span className='badge badge-light-warning'>Stale pool: {inventory.stale_pool_count}</span>
                  )}
                  {classFilter && (
                    <button
                      type='button'
                      className='btn btn-link p-0 fs-8 text-primary'
                      onClick={() => setClassFilter('')}
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className='d-flex align-items-center gap-3 flex-wrap'>
              <Form.Control
                size='sm'
                type='search'
                placeholder='Search number…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{maxWidth: 180}}
              />
              <Form.Check
                type='switch'
                id='reviewOnly'
                label='Review candidates only'
                checked={reviewOnly}
                onChange={(e) => setReviewOnly(e.target.checked)}
              />
              <Button variant='light-primary' size='sm' onClick={exportCsv}>Export CSV</Button>
              <Button variant='primary' size='sm' onClick={openBuy}>Add number to pool</Button>
            </div>
          </Card.Header>
          <Card.Body className='p-0'>
            {iLoading && !inventory ? (
              <div className='p-6'><Spinner animation='border' size='sm' /></div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed align-middle gy-2 mb-0'>
                  <thead>
                    <tr className='fw-bold text-muted' style={{cursor: 'pointer'}}>
                      <th className='ps-6' onClick={() => onSort('phone_number')}>Number{arrow('phone_number')}</th>
                      <th onClick={() => onSort('type')}>Type{arrow('type')}</th>
                      <th onClick={() => onSort('state')}>State{arrow('state')}</th>
                      <th onClick={() => onSort('date_created')}>Purchased{arrow('date_created')}</th>
                      <th onClick={() => onSort('rental_estimate')}>Rental/mo{arrow('rental_estimate')}</th>
                      <th onClick={() => onSort('classification')}>Used for{arrow('classification')}</th>
                      <th onClick={() => onSort('owner')}>Owner / Last used{arrow('owner')}</th>
                      <th onClick={() => onSort('group')}>Group{arrow('group')}</th>
                      {isSuperAdmin && (
                        <th onClick={() => onSort('organization')}>Organization{arrow('organization')}</th>
                      )}
                      <th className='text-end pe-6'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((n) => (
                      <tr key={n.phone_number}>
                        <td className='ps-6 fw-bold'>{n.phone_number}</td>
                        <td>
                          <span className={`badge ${n.type === 'tollfree' ? 'badge-light-info' : 'badge-light-secondary'}`}>{n.type}</span>
                        </td>
                        <td>{stateName(n.state)}</td>
                        <td className='text-muted'>{dateOnly(n.date_created)}</td>
                        <td>{money(n.rental_estimate)}</td>
                        <td>
                          <span className={`badge ${CLASS_BADGE[n.classification]}`}>{CLASS_LABEL[n.classification]}</span>
                          {n.stale && <span className='badge badge-light-warning ms-1'>stale</span>}
                        </td>
                        <td className='text-muted'>
                          {n.owner || (n.last_used ? `used ${n.last_used.slice(0, 10)}` : <span className='text-muted'>—</span>)}
                        </td>
                        <td className='text-muted'>{n.group || <span className='text-muted'>—</span>}</td>
                        {isSuperAdmin && (
                          <td className='text-muted'>{n.organization || <span className='text-muted'>—</span>}</td>
                        )}
                        <td className='text-end pe-6'>
                          <Button
                            variant='light-danger'
                            size='sm'
                            disabled={busy === n.phone_number}
                            onClick={() => handleRelease(n)}
                          >
                            {busy === n.phone_number ? <Spinner animation='border' size='sm' /> : 'Release'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(pageCount > 1 || filtered.length === 0) && (
                  <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 px-6 py-3'>
                    <span className='text-muted fs-8'>
                      {filtered.length === 0
                        ? 'No numbers match'
                        : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                    </span>
                    {pageCount > 1 && (
                      <div className='d-flex align-items-center gap-2'>
                        <Button variant='light' size='sm' disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                          Previous
                        </Button>
                        <span className='text-muted fs-8'>{page} / {pageCount}</span>
                        <Button variant='light' size='sm' disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        <Modal show={showBuy} onHide={closeBuy} centered>
          <Modal.Header closeButton>
            <Modal.Title>{bought ? 'Numbers added to the pool' : 'Add numbers to the pool'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!bought ? (
              <>
                <div className='text-muted fs-7 mb-4'>
                  Buy local-presence numbers and add them to VICIdial's outbound Caller-ID pool
                  (rotated by the lead's state). No burned number required.
                </div>
                <Row className='g-3'>
                  <Col sm={12}>
                    <Form.Label className='small text-muted mb-1'>State</Form.Label>
                    <Form.Select value={poolState} onChange={(e) => setPoolState(e.target.value)}>
                      <option value=''>Select a state…</option>
                      {US_STATES.map((s: any) => (
                        <option key={s.code || s.value} value={s.code || s.value}>
                          {s.name || s.label} ({s.code || s.value})
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col sm={6}>
                    <Form.Label className='small text-muted mb-1'>Area code (optional)</Form.Label>
                    <Form.Control
                      placeholder='e.g. 305'
                      value={poolArea}
                      onChange={(e) => setPoolArea(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                    />
                  </Col>
                  <Col sm={6}>
                    <Form.Label className='small text-muted mb-1'>Quantity</Form.Label>
                    <Form.Control
                      type='number'
                      min={1}
                      max={10}
                      value={poolQty}
                      onChange={(e) => setPoolQty(e.target.value)}
                    />
                  </Col>
                </Row>
                <div className='text-muted fs-8 mt-3'>
                  Est. cost ≈ ${(Math.max(1, Math.min(10, parseInt(poolQty || '1', 10) || 1)) * 1.15).toFixed(2)}/mo
                  {' '}(max 10 per purchase).
                </div>
              </>
            ) : (
              <>
                <div className='alert alert-success py-3'>
                  Bought <b>{bought.count}</b> number(s) for <b>{stateName(bought.state_id)}</b> — added to the pool.
                </div>
                <div className='table-responsive'>
                  <table className='table table-row-dashed align-middle mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th>Number</th>
                        <th>State</th>
                        <th className='text-end'>Cost/mo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(bought.purchased || []).map((p: any) => (
                        <tr key={p.e164}>
                          <td className='fw-bold'>{p.e164}</td>
                          <td>{stateName(p.state_id)}</td>
                          <td className='text-end'>{money(p.cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className='fw-bolder border-top'>
                        <td>Total</td>
                        <td></td>
                        <td className='text-end'>{money(bought.total_cost)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {(bought.warnings || []).length > 0 && (
                  <div className='alert alert-warning py-2 mt-3 fs-8 mb-0'>
                    {(bought.warnings || []).map((w: string, i: number) => <div key={i}>{w}</div>)}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {!bought ? (
              <>
                <Button variant='light' onClick={closeBuy} disabled={buyingPool}>Cancel</Button>
                <Button variant='primary' onClick={handleAddToPool} disabled={buyingPool || !poolState}>
                  {buyingPool ? <Spinner animation='border' size='sm' /> : 'Buy & add to pool'}
                </Button>
              </>
            ) : (
              <>
                <Button variant='light' onClick={() => setBought(null)}>Buy more</Button>
                <Button variant='primary' onClick={closeBuy}>Done</Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
    </div>
  )
}
